import { memo, React, useCallback, useMemo, useState } from 'react'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Col, Form, Modal, Row, Space, Tooltip, Typography } from 'antd'
import { get, isEqual, isNil, map } from 'lodash'
import PropTypes from 'prop-types'

import { useQueryParams } from '../../../core/hooks'
import { areEqualByProps } from '../../../core/utils'

import { FormInput, FormSelect } from '../../../common/components/sections'
import { PAGINATION, PERMISSIONS } from '../../../common/constants'
import {
  useFormDirty,
  useLoggedInUser,
  useUserHasPermissions,
} from '../../../common/hooks'

import { DICTIONARY_WORD_TYPES } from '../../constants'
import { dictionaryWordPropType } from '../../prop-types'
import { getPaginationForDictionaryType } from '../../utils'

const DictionaryTypeNodeTitle = ({
  node,
  languageOptions,
  editable,
  onCreate,
  isCreateLoading,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)
  const [isCreateModalShown, setIsCreateModalShown] = useState(false)

  const hasCreatePermission = useUserHasPermissions(
    PERMISSIONS.DICTIONARY_WORD.CREATE,
  )

  const {
    settings: { pageSize: userPageSize },
  } = useLoggedInUser()
  const { setQueryParams, ...rest } = useQueryParams()

  const dictionaryType = useMemo(
    () => get(DICTIONARY_WORD_TYPES, node.type),
    [node.type],
  )

  const [, pageSize] = useMemo(
    () => getPaginationForDictionaryType(rest, dictionaryType, userPageSize),
    [rest, dictionaryType, userPageSize],
  )

  const isCreateButtonShown = useMemo(
    () => editable && hasCreatePermission && !isNil(onCreate),
    [editable, hasCreatePermission, onCreate],
  )

  const handleConfirmCreate = useCallback(() => {
    form.validateFields().then(({ dictionaryWords }) => {
      const transformedValues = map(dictionaryWords, value => ({
        type: node.type,
        term: value.term,
        languageId: value.languageId,
      }))

      onCreate(transformedValues, pageSize)
      resetForm()

      setQueryParams({
        [dictionaryType.shortName]: [PAGINATION.DEFAULT_PAGE, pageSize],
        searchValue: null,
        languageIds: null,
      })

      setIsCreateModalShown(false)
    })
  }, [
    dictionaryType.shortName,
    form,
    node.type,
    onCreate,
    pageSize,
    resetForm,
    setQueryParams,
  ])

  const handleShowCreateModal = useCallback(() => {
    setIsCreateModalShown(true)
  }, [])

  const handleCancelCreate = useCallback(() => {
    setIsCreateModalShown(false)
    resetForm()
  }, [resetForm])

  const handleAddItem = useCallback(
    add => () => {
      add()
    },
    [],
  )
  const handleRemoveItemByName = useCallback(
    (name, remove) => () => {
      remove(name)
    },
    [],
  )

  return (
    <Space>
      <Typography.Text>{node.term}</Typography.Text>

      {isCreateButtonShown && (
        <Tooltip title="Create multiple words">
          <Button
            onClick={handleShowCreateModal}
            size="small"
            shape="circle"
            icon={<PlusOutlined />}
          />
        </Tooltip>
      )}
      <Modal
        visible={isCreateModalShown}
        title={`Create multiple words of type ${dictionaryType.label}`}
        okText="Create"
        cancelText="Cancel"
        okButtonProps={{ disabled: !isDirty }}
        onCancel={handleCancelCreate}
        onOk={handleConfirmCreate}
        confirmLoading={isCreateLoading}
      >
        <Form
          form={form}
          layout="vertical"
          name="createDictionaryWords"
          onFieldsChange={setToDirty}
        >
          <Form.List rules={[{ type: 'array', min: 1 }]} name="dictionaryWords">
            {(fields, { add, remove }, { errors }) => (
              <>
                <Row wrap gutter={[16]}>
                  {map(fields, ({ key, name, fieldKey, ...restField }) => (
                    <Col span={24} key={key}>
                      <Space align="center" size="large">
                        <FormInput
                          name={[name, 'term']}
                          label="Term"
                          placeholder="Input term..."
                          labelAbove
                          rules={[
                            {
                              required: true,
                            },
                          ]}
                          {...restField}
                        />
                        <FormSelect
                          name={[name, 'languageId']}
                          label="Language"
                          placeholder="Input language..."
                          labelAbove
                          showSearch
                          options={languageOptions}
                          {...restField}
                        />
                        <Button
                          icon={<MinusOutlined />}
                          shape="circle"
                          onClick={handleRemoveItemByName(name, remove)}
                        />
                      </Space>
                    </Col>
                  ))}
                  <Col>
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={handleAddItem(add)}
                        icon={<PlusOutlined />}
                      >
                        Add Dictionary Word
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Form.ErrorList errors={errors} />
                </Row>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </Space>
  )
}

DictionaryTypeNodeTitle.propTypes = {
  node: dictionaryWordPropType.isRequired,
  isCreateLoading: PropTypes.bool,
  editable: PropTypes.bool,
  onCreate: PropTypes.func,
  languageOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
}

DictionaryTypeNodeTitle.defaultProps = {
  editable: false,
  isCreateLoading: false,
  onCreate: undefined,
}

export default memo(
  DictionaryTypeNodeTitle,
  ({ node: prevNode, ...restPrevious }, { node: nextNode, ...restNext }) =>
    isEqual(prevNode, nextNode) &&
    areEqualByProps(restPrevious, restNext, ['editable', 'isCreateLoading']),
)
